'use strict';

import { Box3, Color, DoubleSide, Group, LinearSRGBColorSpace, Mesh, ShaderMaterial, Vector3 } from 'three';
import { assignDeep, Buildings, settingsData, threeJsVariables } from '../../../settings';
import { BaseElement } from '../../interfaces/BaseElement';
import { UnitCardClass } from '../../cards/unitCards/UnitCardClass';
import { findSelectionAndCrmData, flatClick } from '../../../functions';
import { hoverOutPreviousSelection, selectionBoxesSetStatusColor } from '../../threejs/raycast';
import { getBuildingLabel } from './buildingLabels';
import { changeLangToCurrent, getCurrentLang } from '../langSwitcher/langSwitcher';
import { Price } from '../../cards/Price';
import { CSS2DObject } from '../../../libs/CSS2DRenderer';
import { markSelectedApartments } from '../../menu/search/search';

const DEFAULT_PARAMS = {
	fields: {
		name: true,
		type: false,
		price: false
	},
	showSold: true
};

export class CylinderBuildingLabels {
	constructor () {
		this.group = null;
		this.params = assignDeep(DEFAULT_PARAMS,config.threejs.label.cylinder || {});
		this.color = new Color();
		this.activeLabel = null;
	}
	
	create() {
		if (!config.features.privateHouseMode) return;
		
		if (!this.group) {
			this.group = new Group();
			this.group.name = 'CylinderLabels';
			// const buildings = Buildings.getBuildingsArray().filter(building => building.info.availableApartments > 0);
			const buildings = Buildings.getBuildingsArray();
			buildings.forEach(building => this.#buildLabel(building));
		}
		
		this.translateLabels();
		
		threeJsVariables.scene.add(this.group);
	}
	
	#buildLabel(building) {
		if (building.info.availableApartments === 0 && !this.params.showSold)
			return;
		
		const box = new Box3();
		const size = new Vector3();
		const center = new Vector3();
		box.setFromObject(building.selectionsGroup);
		box.getCenter(center);
		box.getSize(size);
		
		const container = new BaseElement()
			.addClass('cylinder-building-label')
			.event('click',() => {
				hoverOutPreviousSelection(UnitCardClass.findCard()?.html);
				if (settingsData.buildingDestroyed)
					selectionBoxesSetStatusColor(building.apartments[0], 'lock');
				
				selectionBoxesSetStatusColor(building.apartments[0], 'click');
				flatClick(building.apartments[0]);
			})
			.DOMElement;
		
		const wrapper = new BaseElement()
			.addClass('cylinder-building-label__wrapper')
			.appendTo(container)
			.DOMElement;
		
		new BaseElement()
			.addClass('cylinder-building-label__bubble')
			.appendTo(wrapper)
			// .innerHtml(getBuildingLabel());
		
		const content = new BaseElement()
			.addClass('cylinder-building-label__content')
			.appendTo(wrapper)
			.DOMElement;
		
		new BaseElement()
			.addClass('cylinder-building-label__arrow')
			.appendTo(content);
		
		let isSold;
		
		if (building.info.availableApartments > 0) {
			Object.entries(this.params.fields).forEach(([key,value]) => {
				if (value) {
					switch (key) {
						case 'name':
							const names = building.info.towerName;
							if (names && Array.isArray(names) && names.length) {
								const nameContainer = new BaseElement('span')
									.addClass('cylinder-building-label__item','icon-home2')
									.appendTo(content)
									.DOMElement;
								let current = names.find(data => data.lang === getCurrentLang());
								if (!current) {
									current = names.find(data => data.lang === 'en');
								}
								const name = new BaseElement('span')
									.addClass('cylinder-building-label__text','language-string')
									.addAttribute('data-key',key)
									.innerHtml(current.value)
									.appendTo(nameContainer);
								names.forEach(data => name.addAttribute(`data-lang-${data.lang}`,data.value));
							}
							break;
						case 'price':
							const priceBlock = new BaseElement('span')
								.addClass('cylinder-building-label__item')
								.addAttribute('data-key',key)
								.appendTo(content)
								.DOMElement;
							new Price()
								.addBlock(priceBlock)
								.setPrice(building.info.crm_data[0]);
							break;
						case 'type':
							const type = new BaseElement('span')
								.addClass('cylinder-building-label__item','icon-plan-type')
								.addAttribute('data-key',key)
								.appendTo(content)
								.DOMElement;
							
							new BaseElement('span')
								.addClass('cylinder-building-label__item-text')
								.innerHtml(building.info.crm_data[0].modelName)
								.appendTo(type);
							break;
					}
				}
			});
			isSold = false;
		} else {
			isSold = true;
			const color = config.resources.bundle.selectionBoxes.colorStatus.find(color => color.status === building.info.crm_data[0].status.toLowerCase());
			const bgColor = '#' + this.color.setStyle(`#${color.basic}`,LinearSRGBColorSpace).convertLinearToSRGB().getHexString();
			const bgElement = container.querySelector('.cylinder-building-label__bubble');
			const arrow = container.querySelector('.cylinder-building-label__arrow');
			bgElement.style.background = bgColor;
			arrow.style.background = bgColor;
			
			const item = new BaseElement('span')
				.addClass('cylinder-building-label__item')
				.appendTo(content)
				.DOMElement;
			
			new BaseElement('span')
				.addClass('cylinder-building-label__text','language-string')
				.addAttribute('data-dictionary',building.info.crm_data[0].status)
				.innerHtml(changeLangToCurrent(building.info.crm_data[0].status))
				.appendTo(item);
		}
		
		const label = new CSS2DObject(container);
		label.name = `labelTower-${building.info.labelName}`;
		label.position.set(center.x,size.y,center.z);
		label.userData.buildingEngineId = building.name;
		label.userData.isSold = isSold;
		this.group.add(label);
	}
	
	translateLabels() {
		if (!config.features.privateHouseMode) return;
		this.group.children.forEach(label => {
			const text = label.element.querySelector('.cylinder-building-label__text');
			if (text) {
				const dictionaryKey = text.getAttribute('data-dictionary');
				if (dictionaryKey) {
					text.innerHTML = changeLangToCurrent(dictionaryKey);
				} else {
					text.innerHTML = text.getAttribute(`data-lang-${getCurrentLang()}`)
				}
			}
		});
	}
	
	updateResults() {
		if (!config.features.privateHouseMode) return;
		const buildings = settingsData.searchResult.map(crm => crm.buildingEngineId);
		const availableLabels = this.group.children.filter(label => !label.userData.isSold);
		availableLabels.forEach(label => {
			if (buildings.includes(label.userData.buildingEngineId)) {
				UnitCardClass.cards.forEach(card => {
					if (card.buildingEngineId === label.userData.buildingEngineId) {
						markSelectedApartments(card.engineId);
					}
				});
				label.visible = true;
			} else {
				if (!label.element.classList.contains('cylinder-building-label--clicked')) {
					label.visible = false;
				} else {
					UnitCardClass.cards.forEach(card => {
						if (card.buildingEngineId === label.userData.buildingEngineId) {
							const {selectedMesh} = findSelectionAndCrmData(card.engineId);
							if (selectedMesh) {
								selectionBoxesSetStatusColor(selectedMesh, 'unlock');
							}
						}
					});
				}
			}
		});
	}
	
	markLabel(engineId,state) {
		if (!config.features.privateHouseMode) return;

		const id = engineId.split('_')[0];
		const availableLabels = this.group.children.filter(label => !label.userData.isSold);
		const label = availableLabels.find(label => label.userData.buildingEngineId === id);
		if (label) {
			const building = Buildings.getBuilding(id);
			const crm = building.info.crm_data[0];
			const bgElement = label.element.querySelector('.cylinder-building-label__bubble');
			const arrow = label.element.querySelector('.cylinder-building-label__arrow');
			if (state === 'click') {
				const color = config.resources.bundle.selectionBoxes.colorStatus.find(color => color.status === crm.status.toLowerCase());
				const bgColor = '#' + this.color.setStyle(`#${color.basic}`,LinearSRGBColorSpace).convertLinearToSRGB().getHexString();
				bgElement.style.background = bgColor;
				arrow.style.background = bgColor;
				label.element.classList.add('cylinder-building-label--clicked');
				label.visible = true;
				this.activeLabel = label;
			} else {
				bgElement.style.background = '';
				arrow.style.background = '';
				label.element.classList.remove('cylinder-building-label--clicked');
				if (this.activeLabel === label) {
					this.activeLabel = null;
				}
			}
			
			setTimeout(() => {
				this.updateResults();
				this.updateEffect();
				this.updateHighlight(state);
			},0);
		}
	}
	
	updateEffect() {
		const effect = threeJsVariables.composer.getPass('maskObjectPass');
		if (this.activeLabel) {
			const building = Buildings.getBuilding(this.activeLabel.userData.buildingEngineId);
			effect.setObject(building.selectionsGroup);
			effect.active = true;
		} else {
			effect.active = false;
		}
	}

	updateHighlight(state) {
		if (this.activeLabel) {
			if (state !== 'click') return;
			const bbox = Buildings.getBuilding(this.activeLabel.userData.buildingEngineId);
			let building;
			bbox.selectionsGroup.traverse(object => {
				if (object.isMesh && !building && object.userData.name.includes(this.activeLabel.userData.buildingEngineId)) {
					building = object;
				}
			});
			const highlightPrev = threeJsVariables.scene.getObjectByName('highlight');
			if (highlightPrev) {
				threeJsVariables.scene.remove(highlightPrev);
				highlightPrev.geometry.dispose();
				highlightPrev.material.dispose();
			}
			if (!building) {
				console.log('selection box model wasnt found');
				return;
			}
			const colorStatus = config.resources.bundle.selectionBoxes.colorStatus;
			const color = colorStatus.find(c => c.status === 'available');
			const highlight = building.clone(true);
			highlight.name = 'highlight';
			highlight.userData = { name: 'highlight' };
			highlight.material = new ShaderMaterial({
				uniforms: {
					color: { value: new Color().setStyle(`#${color.hover}`,LinearSRGBColorSpace) },
					baseHeight: { value: -0.5 },
					gradHeight: { value: 4.5 }
				},
				vertexShader: `
					varying vec2 vUv;

					void main() {
						vUv = position.xy;
						gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
					}
				`,
				fragmentShader: `
					uniform vec3 color;
					uniform float baseHeight;
					uniform float gradHeight;
					varying vec2 vUv;

					void main() {
						float alpha = 1.0 - (vUv.y - baseHeight) / gradHeight;
						if (vUv.y <= 0.01) {
							alpha = 0.0;
						}
						gl_FragColor = vec4(color, alpha);
					}
				`,
				transparent: true,
				side: DoubleSide,
				depthWrite: false
			});
			building.getWorldPosition(highlight.position);
			building.getWorldScale(highlight.scale);
			building.getWorldQuaternion(highlight.quaternion);
			threeJsVariables.scene.add(highlight);
		} else {
			const highlight = threeJsVariables.scene.getObjectByName('highlight');
			if (highlight) {
				threeJsVariables.scene.remove(highlight);
				highlight.geometry.dispose();
				highlight.material.dispose();
			}
		}
	}
	
	remove() {
		if (this.group && this.group.parent) {
			this.group.removeFromParent();
			this.group.traverse(el => {
				if (el.isCSS2DObject) {
					el.element.remove();
				}
			})
		}
	}
}
